import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type User = {
  id: string;
  displayName: string;
  role: "admin" | "member";
};
export type AuthStatus = {
  needsSetup: boolean;
  user: User | null;
  accounts: User[];
};

const AuthContext = createContext<{
  status?: AuthStatus;
  loading: boolean;
  refresh: () => Promise<unknown>;
}>({ loading: true, refresh: async () => undefined });

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["auth"],
    queryFn: () => api<AuthStatus>("/api/auth/status"),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  return (
    <AuthContext.Provider
      value={{
        status: query.data,
        loading: query.isLoading,
        refresh: () => client.invalidateQueries({ queryKey: ["auth"] }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// The auth hook intentionally shares its provider's module.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext);
}
